import AppShell from '@/components/AppShell'
import { useEffect, useMemo, useState } from 'react'
import { Can } from '@/lib/authz'
import { get, set } from '@/lib/storage'
import { toCSV, downloadCSV } from '@/lib/csv'
import { useNotifications } from '@/lib/notifications'

type Line = { id:string; desc:string; tons:number; pricePerTon:number; logging?:number; trucking?:number; other?:number }
type Settlement = {
  id: string; name: string; customer?: string; mill?: string; job?: string; period?: string;
  lines: Line[];
}
type Snapshot = { id:string; date:string; mill:string; job:string; product:string; rateLogging?:number; rateTrucking?:number; rateProduct?:number; notes?:string }

const KEY = 'settlements'
const RKEY = 'rate-history'

const exampleLine: Line = { id:crypto.randomUUID(), desc:'Pine Sawtimber', tons:27.48, pricePerTon:45, logging:18, trucking:10, other:0 }

export default function Builder(){
  const [all, setAll] = useState<Settlement[]>(get<Settlement[]>(KEY, []))
  const [stl, setStl] = useState<Settlement>({ id: crypto.randomUUID(), name:'New Settlement', customer:'', mill:'', job:'', period:'', lines:[exampleLine] })
  const [snaps] = useState<Snapshot[]>(get<Snapshot[]>(RKEY, []))
  const { push } = useNotifications()

  useEffect(()=>{ set(KEY, all) }, [all])

  const totals = useMemo(()=>{
    const revenue = stl.lines.reduce((s,l)=> s + l.tons*l.pricePerTon, 0)
    const logging = stl.lines.reduce((s,l)=> s + (l.logging||0)*l.tons, 0)
    const trucking = stl.lines.reduce((s,l)=> s + (l.trucking||0)*l.tons, 0)
    const other = stl.lines.reduce((s,l)=> s + (l.other||0)*l.tons, 0)
    const expenses = logging + trucking + other
    const profit = revenue - expenses
    return { revenue, logging, trucking, other, expenses, profit }
  }, [stl])

  function addLine(){ setStl(s=>({...s, lines:[...s.lines, { ...exampleLine, id: crypto.randomUUID(), desc:'New product', tons:0, pricePerTon:0 }]})) }
  function rmLine(id:string){ setStl(s=>({...s, lines: s.lines.filter(l=>l.id!==id)})) }
  function editLine(id:string, k:keyof Line, v:any){
    setStl(s=>({...s, lines: s.lines.map(l=> l.id===id ? ({...l, [k]: (['tons','pricePerTon','logging','trucking','other'].includes(k as string) ? Number(v) : v) }) : l)}))
  }

  function save(){
    setAll(a => [stl, ...a.filter(x=>x.id!==stl.id)])
    push({ title:'Settlement saved', body: stl.name })
  }

  function exportCSV(){
    const headers = ['Desc','Tons','$/Ton','Revenue','Log $/t','Truck $/t','Other $/t']
    const rows = stl.lines.map(l => [
      l.desc, l.tons, l.pricePerTon, (l.tons*l.pricePerTon).toFixed(2),
      l.logging||0, l.trucking||0, l.other||0
    ])
    const summary = [
      [], ['Revenue', totals.revenue.toFixed(2)],
      ['Expenses', totals.expenses.toFixed(2)], ['Profit', totals.profit.toFixed(2)]
    ]
    const csv = toCSV(headers, rows.concat(summary as any))
    downloadCSV(`${stl.name || 'settlement'}.csv`, csv)
  }

  function load(id:string){
    const found = all.find(s=>s.id===id)
    if (found) setStl(JSON.parse(JSON.stringify(found)))
  }
  function remove(id:string){
    setAll(a => a.filter(s=>s.id!==id))
    if (stl.id === id && all.length) setStl(all[0])
  }

  // === Rate suggestions from latest snapshot ===
  function latestSnapshot(mill?:string, job?:string, product?:string): Snapshot | null {
    if (!mill || !job || !product) return null
    const matching = snaps.filter(s =>
      s.mill?.toLowerCase() === mill.toLowerCase() &&
      s.job?.toLowerCase() === job.toLowerCase() &&
      s.product?.toLowerCase() === product.toLowerCase()
    )
    if (!matching.length) return null
    matching.sort((a,b)=> (b.date||'').localeCompare(a.date||''))
    return matching[0]
  }

  function applySuggestedRates(lineId:string){
    const line = stl.lines.find(l=>l.id===lineId)
    if (!line) return
    const snap = latestSnapshot(stl.mill, stl.job, line.desc)
    if (!snap) { push({title:'No matching rate snapshot found', level:'warning'}); return }
    setStl(s=>({
      ...s,
      lines: s.lines.map(l => l.id===lineId
        ? ({ ...l,
              logging: snap.rateLogging ?? l.logging,
              trucking: snap.rateTrucking ?? l.trucking,
              pricePerTon: snap.rateProduct ?? l.pricePerTon
            })
        : l)
    }))
    push({ title:'Applied suggested rates', body:`${snap.mill}/${snap.job}/${snap.product} (${snap.date})` })
  }

  return (
    <AppShell title="Settlement Builder">
      <div className="grid gap-4">
        {/* Header meta */}
        <div className="card">
          <div className="card-body grid grid-cols-1 md:grid-cols-5 gap-3">
            <input className="input" placeholder="Settlement name" value={stl.name} onChange={e=>setStl({...stl,name:e.target.value})}/>
            <input className="input" placeholder="Customer" value={stl.customer||''} onChange={e=>setStl({...stl,customer:e.target.value})}/>
            <input className="input" placeholder="Mill" value={stl.mill||''} onChange={e=>setStl({...stl,mill:e.target.value})}/>
            <input className="input" placeholder="Job" value={stl.job||''} onChange={e=>setStl({...stl,job:e.target.value})}/>
            <input className="input" placeholder="Period (e.g., 2025-09 wk3)" value={stl.period||''} onChange={e=>setStl({...stl,period:e.target.value})}/>
          </div>
        </div>

        {/* Lines */}
        <div className="card">
          <div className="card-header">Line Items</div>
          <div className="card-body grid gap-2">
            {stl.lines.map(l=>(
              <div key={l.id} className="grid grid-cols-12 gap-2">
                <input className="input col-span-3" value={l.desc} onChange={e=>editLine(l.id,'desc',e.target.value)} placeholder="Product name (must match Rate History 'product')" />
                <input className="input col-span-2" type="number" step="0.001" placeholder="Tons" value={l.tons} onChange={e=>editLine(l.id,'tons',e.target.value)}/>
                <input className="input col-span-2" type="number" step="0.01" placeholder="$ / ton" value={l.pricePerTon} onChange={e=>editLine(l.id,'pricePerTon',e.target.value)}/>
                <input className="input col-span-1" type="number" step="0.01" placeholder="Log" value={l.logging||0} onChange={e=>editLine(l.id,'logging',e.target.value)}/>
                <input className="input col-span-1" type="number" step="0.01" placeholder="Truck" value={l.trucking||0} onChange={e=>editLine(l.id,'trucking',e.target.value)}/>
                <input className="input col-span-1" type="number" step="0.01" placeholder="Other" value={l.other||0} onChange={e=>editLine(l.id,'other',e.target.value)}/>
                <button className="btn-secondary col-span-1" onClick={()=>applySuggestedRates(l.id)}>Suggest</button>
              </div>
            ))}
            <Can roles={['Admin','Accountant']}><button className="btn w-fit" onClick={addLine}>Add line</button></Can>
          </div>
        </div>

        {/* Totals */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card title="Revenue" value={totals.revenue} />
          <Card title="Expenses" value={totals.expenses} />
          <Card title="Profit" value={totals.profit} />
        </div>

        {/* Actions + Saved list */}
        <div className="card">
          <div className="card-body flex flex-wrap gap-2 items-center">
            <button className="btn" onClick={save}>Save Settlement</button>
            <button className="btn-secondary" onClick={exportCSV}>Export CSV</button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Saved Settlements</div>
          <div className="card-body grid gap-2">
            {all.length===0 && <div className="text-sm text-slate-600">No saved settlements yet.</div>}
            {all.map(s=>(
              <div key={s.id} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.customer || '-'} • {s.mill || '-'} • {s.job || '-'} • {s.period || '-'}</div>
                </div>
                <div className="flex gap-2">
                  <button className="btn-secondary" onClick={()=>load(s.id)}>Open</button>
                  <button className="btn-secondary" onClick={()=>remove(s.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  )
}

function Card({title, value}:{title:string, value:number}){
  const color = value>=0 ? 'text-forest-700' : 'text-red-600'
  return (
    <div className="card">
      <div className="card-body">
        <div className="text-xs text-slate-500">{title}</div>
        <div className={`text-2xl font-semibold ${color}`}>${value.toFixed(2)}</div>
      </div>
    </div>
  )
}
